
/* ========================================================================== */
/*  FILE: components/home/FeatureCardsClient.tsx                               */
/* ========================================================================== */

"use client";

import React, { useMemo } from "react";
import {
  Calendar,
  ChevronRight,
  Clock,
  CreditCard,
  Headphones,
  QrCode,
  Shield,
  Smartphone,
  Sparkles,
  UtensilsCrossed,
  Zap,
} from "lucide-react";
import Link from "next/link";

type Feature = {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  gradient: string;
  highlights?: string[];
};

export type FeatureCardsClientProps = {
  heading?: string;
  subheading?: string;
  features?: Feature[];
  ctaHref?: string;
  ctaLabel?: string;
  showCta?: boolean;
};

function cn(...classes: Array<string | undefined | null | false>) {
  return classes.filter(Boolean).join(" ");
}

export default function FeatureCardsClient(props: FeatureCardsClientProps) {
  const features: Feature[] = useMemo(
    () =>
      props.features ?? [
        {
          icon: Smartphone,
          title: "Digital Menus",
          description:
            "Browse HD menus with photos, pricing, ingredients, and real-time availability updates.",
          gradient: "from-sky-500/20 via-cyan-500/10 to-transparent",
          highlights: ["Photos & descriptions", "Live availability", "Allergens & tags"],
        },
        {
          icon: Clock,
          title: "Order Ahead",
          description:
            "Place dine-in or pickup orders in seconds. Skip waiting lines and track progress live.",
          gradient: "from-amber-500/20 via-orange-500/10 to-transparent",
          highlights: ["Faster checkout", "Live tracking", "Pickup & dine-in"],
        },
        {
          icon: Calendar,
          title: "Table Booking",
          description:
            "Reserve tables instantly with real-time availability, confirmations, and reminders.",
          gradient: "from-emerald-500/20 via-green-500/10 to-transparent",
          highlights: ["Instant confirmation", "Smart reminders", "Priority slots"],
        },
        {
          icon: Shield,
          title: "Secure Payments",
          description:
            "Pay with mobile money, cards, or cash — using secure, encrypted payment flows.",
          gradient: "from-violet-500/20 via-indigo-500/10 to-transparent",
          highlights: ["Encrypted checkout", "Multiple methods", "Receipts & history"],
        },
        {
          icon: QrCode,
          title: "QR Ordering",
          description:
            "Scan a table QR code to open the menu instantly and order without waiting for a waiter.",
          gradient: "from-fuchsia-500/20 via-pink-500/10 to-transparent",
          highlights: ["No app required", "Fast ordering", "Table-aware cart"],
        },
        {
          icon: CreditCard,
          title: "Unified Wallet",
          description:
            "Track spending, save favorites, and keep receipts in one place for easy reordering.",
          gradient: "from-slate-500/20 via-zinc-500/10 to-transparent",
          highlights: ["Order history", "Favorites", "Reorder in 1 tap"],
        },
        {
          icon: UtensilsCrossed,
          title: "Menu Intelligence",
          description:
            "Discover best sellers, dietary-friendly options, and seasonal specials tailored to you.",
          gradient: "from-orange-500/20 via-amber-500/10 to-transparent",
          highlights: ["Trending dishes", "Diet filters", "Seasonal specials"],
        },
        {
          icon: Headphones,
          title: "24/7 Support",
          description:
            "We help diners and restaurants with fast support, refunds, and order issue handling.",
          gradient: "from-blue-500/20 via-indigo-500/10 to-transparent",
          highlights: ["Fast responses", "Issue resolution", "Transparent refunds"],
        },
      ],
    [props.features]
  );

  const heading = props.heading ?? "Everything You Need";
  const subheading =
    props.subheading ??
    "A complete dining experience platform designed for Malawi — built for speed, clarity, and trust.";
  const ctaHref = props.ctaHref ?? "/vendors";
  const ctaLabel = props.ctaLabel ?? "Explore Restaurants";

  return (
    <section className="relative py-16 sm:py-20 bg-white" aria-label="Features">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(251,191,36,0.10),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,rgba(249,115,22,0.08),transparent_60%)]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-14">
          <div className="inline-flex items-center gap-2 mb-4 rounded-full border border-neutral-200 bg-neutral-50 px-4 py-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            <span className="text-xs sm:text-sm font-semibold text-neutral-700">
              Built for diners & restaurants
            </span>
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-neutral-600 border border-neutral-200">
              <Zap className="h-3.5 w-3.5 text-amber-500" />
              Fast
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-neutral-900">
            {heading}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-neutral-600 leading-relaxed">
            {subheading}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {features.map((f, idx) => {
            const Icon = f.icon;
            return (
              <div
                key={`${f.title}-${idx}`}
                className={cn(
                  "group relative rounded-2xl border border-neutral-200 bg-white/70 backdrop-blur",
                  "shadow-[0_10px_40px_-20px_rgba(0,0,0,0.25)]",
                  "transition-transform duration-300 hover:-translate-y-1"
                )}
              >
                <div className={cn("absolute inset-0 rounded-2xl bg-gradient-to-br", f.gradient)} />
                <div className="relative p-6 sm:p-7">
                  <div className="flex items-start gap-4">
                    <div
                      className={cn(
                        "flex h-12 w-12 items-center justify-center rounded-2xl border",
                        "border-neutral-200 bg-white shadow-sm"
                      )}
                    >
                      <Icon className="h-6 w-6 text-neutral-800" />
                    </div>

                    <div className="flex-1">
                      <h3 className="text-lg sm:text-xl font-bold text-neutral-900">
                        {f.title}
                      </h3>
                      <p className="mt-2 text-sm sm:text-base text-neutral-600 leading-relaxed">
                        {f.description}
                      </p>
                    </div>
                  </div>

                  {f.highlights?.length ? (
                    <ul className="mt-5 space-y-2">
                      {f.highlights.slice(0, 3).map((h, i) => (
                        <li
                          key={`${f.title}-h-${i}`}
                          className="flex items-center gap-2 text-sm text-neutral-700"
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          <span className="line-clamp-1">{h}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="mt-6 flex items-center justify-between">
                    <span className="text-xs text-neutral-500">
                      Feature {idx + 1}/{features.length}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 group-hover:text-amber-600">
                      Learn more <ChevronRight className="h-4 w-4" />
                    </span>
                  </div>

                  <div className="mt-5 h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />
                </div>
              </div>
            );
          })}
        </div>

        {props.showCta !== false && (
          <div className="mt-14 flex justify-center">
            <Link
              href={ctaHref}
              className={cn(
                "group inline-flex items-center gap-3 rounded-2xl px-8 py-4",
                "bg-neutral-900 text-white font-extrabold shadow-xl",
                "hover:bg-neutral-800 transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
              )}
            >
              <span>{ctaLabel}</span>
              <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}